import { assertPackedScoreFitsU32 } from "./packed/evaluator.js";
import { GPU_DUTY_ALGORITHM_VERSION } from "./packed/shadow.js";
import { PACKED_DUTY_VERSION, PACKED_UNASSIGNED, SHIFT_FLAG_NIGHT, SHIFT_FLAG_OFF, SHIFT_PATTERN_MASK, SHIFT_PATTERN_SHIFT, } from "./packed/types.js";
export const NUMERIC_DUTY_PROTOCOL_VERSION = 1;
/** Additive long execution contract; v1 bytes and 31-day limits remain unchanged. */
export const NUMERIC_DUTY_EXTENDED_PROTOCOL_VERSION = 2;
export const NUMERIC_DUTY_RULES_VERSION = "packed-duty-rules-v2";
export const NUMERIC_DUTY_LIMITS = Object.freeze({ nurses: 50, shifts: 32, days: 31 });
export const NUMERIC_DUTY_EXTENDED_LIMITS = Object.freeze({ nurses: 50, shifts: 32, days: 62 });
export class NumericDutyInputError extends Error {
    constructor(field) {
        super(`Invalid numeric duty input: ${field}`);
        this.field = field;
        this.name = "NumericDutyInputError";
    }
}
export function assertNumericRecord(value, keys, field) {
    if (!value ||
        typeof value !== "object" ||
        Array.isArray(value) ||
        Object.getPrototypeOf(value) !== Object.prototype ||
        Object.keys(value).length !== keys.length ||
        !keys.every((key) => Object.keys(value).includes(key)))
        throw new NumericDutyInputError(field);
}
function integer(value, min, max, field) {
    if (!Number.isSafeInteger(value) || value < min || value > max)
        throw new NumericDutyInputError(field);
    return value;
}
function words(value, length, field, max = PACKED_UNASSIGNED) {
    if (!Array.isArray(value) || value.length !== length)
        throw new NumericDutyInputError(field);
    const result = new Uint32Array(length);
    for (let index = 0; index < length; index++)
        result[index] = integer(value[index], 0, max, `${field}.${index}`);
    return result;
}
/** Strict, bounded JSON boundary; synthetic metadata is never a business mapping. */
export function decodeNumericDutyInput(value) {
    assertNumericRecord(value, [
        "protocolVersion",
        "engineVersion",
        "rulesVersion",
        "nurseCount",
        "shiftCount",
        "dayCount",
        "requirements",
        "fixedAssignments",
        "availability",
        "availableShiftBits",
        "shiftFlags",
        "rules",
    ], "input");
    if ((value.protocolVersion !== NUMERIC_DUTY_PROTOCOL_VERSION &&
        value.protocolVersion !== NUMERIC_DUTY_EXTENDED_PROTOCOL_VERSION) ||
        value.engineVersion !== GPU_DUTY_ALGORITHM_VERSION ||
        value.rulesVersion !== NUMERIC_DUTY_RULES_VERSION)
        throw new NumericDutyInputError("version");
    const nurseCount = integer(value.nurseCount, 1, NUMERIC_DUTY_LIMITS.nurses, "nurseCount");
    const shiftCount = integer(value.shiftCount, 1, NUMERIC_DUTY_LIMITS.shifts, "shiftCount");
    // Disjoint ranges keep decode/encode normalization and existing input hashes unambiguous.
    const extended = value.protocolVersion === NUMERIC_DUTY_EXTENDED_PROTOCOL_VERSION;
    const dayCount = integer(value.dayCount, extended ? NUMERIC_DUTY_LIMITS.days + 1 : 1, extended ? NUMERIC_DUTY_EXTENDED_LIMITS.days : NUMERIC_DUTY_LIMITS.days, "dayCount");
    const cells = nurseCount * dayCount;
    const requirements = words(value.requirements, dayCount * shiftCount, "requirements", NUMERIC_DUTY_LIMITS.nurses);
    const fixedAssignments = words(value.fixedAssignments, cells, "fixedAssignments");
    for (const shift of fixedAssignments)
        if (shift !== PACKED_UNASSIGNED && shift >= shiftCount)
            throw new NumericDutyInputError("fixedAssignments.shift");
    const availability = words(value.availability, cells, "availability", 1);
    const availableShiftBits = words(value.availableShiftBits, nurseCount, "availableShiftBits");
    const mask = shiftCount === 32 ? PACKED_UNASSIGNED : (2 ** shiftCount - 1) >>> 0;
    for (const bits of availableShiftBits)
        if ((bits & ~mask) !== 0)
            throw new NumericDutyInputError("availableShiftBits.padding");
    const shiftFlags = words(value.shiftFlags, shiftCount, "shiftFlags");
    for (let shift = 0; shift < shiftCount; shift++) {
        const flags = shiftFlags[shift];
        const pattern = (flags & SHIFT_PATTERN_MASK) >>> SHIFT_PATTERN_SHIFT;
        if ((flags & ~(SHIFT_PATTERN_MASK | SHIFT_FLAG_OFF | SHIFT_FLAG_NIGHT)) !== 0 ||
            pattern > 4 ||
            Boolean(flags & SHIFT_FLAG_OFF) !== (pattern === 4) ||
            ((flags & SHIFT_FLAG_OFF) !== 0 && (flags & SHIFT_FLAG_NIGHT) !== 0))
            throw new NumericDutyInputError("shiftFlags.flags");
        if ((flags & SHIFT_FLAG_OFF) !== 0)
            for (let day = 0; day < dayCount; day++)
                if (requirements[day * shiftCount + shift] !== 0)
                    throw new NumericDutyInputError("requirements.off");
    }
    const rules = words(value.rules, 3, "rules");
    if (rules[0] < 1 || rules[1] < 1 || (rules[2] & ~15) !== 0)
        throw new NumericDutyInputError("rules.flags-or-limit");
    const input = {
        version: PACKED_DUTY_VERSION,
        nurseCount,
        shiftCount,
        dayCount,
        shiftBitWords: 1,
        requirements,
        fixedAssignments,
        availability,
        availableShiftBits,
        shiftFlags,
        rules,
        metadata: {
            dates: Array.from({ length: dayCount }, (_, index) => `day:${index}`),
            nurseIds: Array.from({ length: nurseCount }, (_, index) => `nurse:${index}`),
            shiftIds: Array.from({ length: shiftCount }, (_, index) => `shift:${index}`),
        },
    };
    assertPackedScoreFitsU32(input);
    return input;
}
/** Explicit projection prevents metadata and future application fields from leaking. */
export function encodeNumericDutyInput(input) {
    if (input.version !== PACKED_DUTY_VERSION || input.shiftBitWords !== 1)
        throw new NumericDutyInputError("packed.version-or-layout");
    const value = {
        protocolVersion: input.dayCount > NUMERIC_DUTY_LIMITS.days
            ? NUMERIC_DUTY_EXTENDED_PROTOCOL_VERSION
            : NUMERIC_DUTY_PROTOCOL_VERSION,
        engineVersion: GPU_DUTY_ALGORITHM_VERSION,
        rulesVersion: NUMERIC_DUTY_RULES_VERSION,
        nurseCount: input.nurseCount,
        shiftCount: input.shiftCount,
        dayCount: input.dayCount,
        requirements: Array.from(input.requirements),
        fixedAssignments: Array.from(input.fixedAssignments),
        availability: Array.from(input.availability),
        availableShiftBits: Array.from(input.availableShiftBits),
        shiftFlags: Array.from(input.shiftFlags),
        rules: Array.from(input.rules),
    };
    decodeNumericDutyInput(value);
    return value;
}
//# sourceMappingURL=numeric.js.map